"use client"

import { useMemo, useState } from "react"
import type { RawImageRef } from "@kraahl/contract"
import { resolveStorageUrl } from "@/lib/storage/public-url"
import dynamic from "next/dynamic"
import { useRouter } from "next/navigation"
import { ChevronLeft } from "lucide-react"

import { BookmarkLoginModal, type BookmarkModalListing } from "@/components/search/product/bookmark-login-modal"
import { useSavedState } from "@/hooks/use-saved-state"
import { cn } from "@/lib/utils"
import MobileProductPage from "./mobile-product-page"
import ListingGalleryDesktop from "@/components/detailed-listing-view/listing-gallery-desktop"
import ProductDetails from "@/components/detailed-listing-view/product-details"
import { AuctionWinnerCheckoutSheet } from "@/components/detailed-listing-view/auction-winner-checkout-sheet"
import type { AuctionData } from "./auction-panel"
import type { TcgCardDetailsData } from "./tcg-card-details"
import type { ListingShareMeta } from "@/lib/story-cards"

const ListingSuggestionsLazy = dynamic(
  () => import("@/components/detailed-listing-view/listing-suggestions").then((m) => ({ default: m.ListingSuggestions })),
  { ssr: false }
)

interface ProductDetailViewProps {
  title?: string
  price?: string
  currency?: string | null
  condition?: string
  grading?: string
  description?: string
  /** RESOLVED absolute URLs — gallery / share / OG. */
  images?: string[]
  /**
   * The same images UN-resolved. Only the auction-winner checkout sheet reads
   * them: its snapshot is persisted into `order_items.image_url`, so it must
   * not receive display URLs (handoff §9).
   */
  imageRefs?: RawImageRef[]
  itemId?: string
  location?: string
  createdAt?: string
  seller?: {
    id: string
    username: string
    /** `profiles.avatar_url` AS STORED — resolve before any URL sink. */
    avatar_url?: RawImageRef
    followers_count: number
    is_following: boolean
    sales_count?: number
    checkout_note?: string | null
  }
  currentUserId?: string
  stock?: number
  isSaved?: boolean
  savesCount?: number
  scheduledPublishAt?: string | null
  sellerBanned?: boolean
  isUnlisted?: boolean
  auction?: AuctionData | null
  auctionCountryBanWarning?: string | null
  cardDetails?: TcgCardDetailsData | null
  isPreorder?: boolean
  listingFormat?: string | null
  // Phase 3 (2026-06-29): `acceptsOffers` prop dropped — derive from
  // `listingFormat` via `isOfferEnabledFormat()` from `@kraahl/contract`.
  autoDeclineBelow?: number | null
  preorderDeadline?: string | null
  preorderEstimatedDelivery?: string | null
  preorderMinOrders?: number | null
  preorderMaxOrders?: number | null
  preorderCurrentOrders?: number | null
  preorderStatus?: string | null
}

export default function ProductDetailView({
  title = "No title",
  price = "0",
  currency = null,
  condition = "unknown",
  grading = "N/A",
  description = "",
  images = [],
  imageRefs = [],
  itemId = "",
  location = "",
  createdAt = "",
  seller,
  currentUserId,
  stock,
  isSaved = false,
  savesCount,
  scheduledPublishAt = null,
  sellerBanned = false,
  isUnlisted = false,
  auction = null,
  auctionCountryBanWarning = null,
  cardDetails = null,
  isPreorder = false,
  listingFormat = null,
  autoDeclineBelow = null,
  preorderDeadline = null,
  preorderEstimatedDelivery = null,
  preorderMinOrders = null,
  preorderMaxOrders = null,
  preorderCurrentOrders = null,
  preorderStatus = null,
}: ProductDetailViewProps) {
  const router = useRouter()
  const [showBookmarkModal, setShowBookmarkModal] = useState(false)
  const [auctionCheckoutOpen, setAuctionCheckoutOpen] = useState(false)
  const onPayAsWinner = () => setAuctionCheckoutOpen(true)

  // Use the saved state hook instead of local state
  const { saved, loading: saving, toggleSaved } = useSavedState({
    itemId,
    itemType: "listing",
    initialSaved: isSaved,
    currentUserId,
  })

  const handleBack = () => {
    if (typeof window === "undefined") {
      router.push("/feed")
      return
    }
    const referrer = document.referrer
    if (!referrer) {
      router.push("/feed")
      return
    }
    try {
      const referrerUrl = new URL(referrer)
      if (referrerUrl.origin !== window.location.origin) {
        router.push("/feed")
        return
      }
    } catch {
      router.push("/feed")
      return
    }
    if (window.history.length <= 1) {
      router.push("/feed")
      return
    }
    router.back()
  }

  const handleSaveItem = async () => {
    if (!currentUserId) {
      setShowBookmarkModal(true)
      return
    }

    await toggleSaved()
  }


  const handleFollowChange = (userId: string, next: boolean) => {
    if (seller) {
      seller.is_following = next
    }
  }

  /**
   * `seller` for the DISPLAY subtree (`MobileProductPage` / `ProductDetails`),
   * with the avatar already resolved.
   *
   * The resolve is hoisted here rather than pushed into the leaves because the
   * chain below dives into legacy `string`-typed shapes that are not part of
   * this migration: `OrderPartyCard.avatarUrl` forwards the value into chat
   * (`initialOtherProfile`) and listing-card payloads, and `ReportMenu` feeds
   * `reports.reported_avatar_url` — a column that stays ABSOLUTE by design.
   * Every consumer down there wants the absolute form, so one resolve at the
   * fork is both correct and the smallest cut.
   *
   * The RAW `seller.avatar_url` is deliberately NOT used below: it goes only to
   * `AuctionWinnerCheckoutSheet`, whose snapshot `createOrder` persists
   * verbatim (display != persistence, handoff §9).
   */
  const displaySeller = useMemo(
    () =>
      seller
        ? {
            ...seller,
            // `?? undefined`: the nullable overload widens to `string | null`,
            // but `avatar_url` is `RawImageRef | undefined` — never null.
            avatar_url: resolveStorageUrl(seller.avatar_url) ?? undefined,
          }
        : undefined,
    [seller],
  )

  const listingForModal: BookmarkModalListing = {
    id: itemId,
    title,
    price,
    currency,
    image_urls: images,
    description,
    condition,
    grade: grading,
    created_at: createdAt,
    username: seller?.username,
    // Display shape (mirrors the listing-card props) — resolve. The RAW ref
    // goes to the auction-checkout sheet below, which persists it.
    avatar_url: displaySeller?.avatar_url,
    scheduledPublishAt,
  }

  const listingMeta: ListingShareMeta = {
    listingFormat,
    isPreorder,
    scheduledPublishAt,
    auction: auction
      ? {
          startPrice: auction.startPrice,
          highestBid: auction.highestBid ?? null,
          bidCount: auction.bidCount ?? 0,
          endAt: auction.endAt ?? null,
          status: auction.status ?? null,
        }
      : null,
    preorder: isPreorder
      ? {
          deadline: preorderDeadline,
          estimatedDelivery: preorderEstimatedDelivery,
          minOrders: preorderMinOrders,
          maxOrders: preorderMaxOrders,
          currentOrders: preorderCurrentOrders,
          status: preorderStatus,
        }
      : null,
  }

  return (
    <>
      {/* Mobile: Version dédiée et optimisée */}
      <div className="md:hidden">
        <MobileProductPage
          title={title}
          price={price}
          currency={currency}
          condition={condition}
          grading={grading}
          description={description}
          images={images}
          itemId={itemId}
          location={location}
          createdAt={createdAt}
          seller={displaySeller}
          currentUserId={currentUserId}
          stock={stock}
          isSaved={isSaved}
          savesCount={savesCount}
          scheduledPublishAt={scheduledPublishAt}
          sellerBanned={sellerBanned}
          isUnlisted={isUnlisted}
          auction={auction}
          onPayAsWinner={onPayAsWinner}
          auctionCountryBanWarning={auctionCountryBanWarning}
          cardDetails={cardDetails}
          listingMeta={listingMeta}
          isPreorder={isPreorder}
          listingFormat={listingFormat}
          autoDeclineBelow={autoDeclineBelow}
        />
      </div>

      {/* Tablet & Desktop: Version existante */}
      <div className="hidden md:block">
        <BookmarkLoginModal
          open={showBookmarkModal}
          onClose={() => setShowBookmarkModal(false)}
          listing={listingForModal}
        />
        {/* Tablet + Desktop: single unified subtree (Path B step 1 — triplet
            handoff 2026-07-23). One responsive grid (2-col at md → 12-col at
            lg) rendering ONE <ProductDetails>, so OrderPartyCard + its data
            hooks mount once for md+lg instead of once per breakpoint. */}
        <div className="w-full bg-card px-0 pb-4">
          <div className="w-full py-6">
            <div className="max-w-[1200px] lg:max-w-[1600px] mx-auto px-6 lg:px-8 mb-4 lg:mb-6">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-muted rounded-full"
                aria-label="Go back"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
            <div className="max-w-[1200px] lg:max-w-[1600px] mx-auto grid grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 px-6 lg:px-8 items-start">
              <div
                className={cn(
                  "lg:col-span-7 xl:col-span-7 lg:max-w-[900px]",
                  auction && "md:sticky md:top-[calc(var(--header-height,3.5rem)+1.5rem)] md:self-start",
                )}
              >
                <ListingGalleryDesktop
                  images={images}
                  title={title}
                  isSaved={saved}
                  saving={saving}
                  onSaveItemAction={handleSaveItem}
                  itemId={itemId}
                  price={price}
                  currency={currency}
                  scheduledPublishAt={scheduledPublishAt}
                  sellerUsername={seller?.username}
                  savesCount={savesCount}
                  listingMeta={listingMeta}
                />
              </div>
              <div
                className={cn(
                  "self-start lg:col-span-5 xl:col-span-5 lg:max-w-[600px]",
                  !auction && "md:sticky md:top-[calc(var(--header-height,3.5rem)+1.5rem)]",
                )}
              >
                <ProductDetails
                  title={title}
                  price={price}
                  currency={currency}
                  condition={condition}
                  grading={grading}
                  description={description}
                  location={location}
                  createdAt={createdAt}
                  images={images}
                  shippingMethods={[]}
                  seller={displaySeller!}
                  currentUserId={currentUserId}
                  itemId={itemId}
                  stock={stock}
                  onFollowChangeAction={handleFollowChange}
                  scheduledPublishAt={scheduledPublishAt}
                  sellerBanned={sellerBanned}
                  isUnlisted={isUnlisted}
                  auction={auction}
                  onPayAsWinner={onPayAsWinner}
                  auctionCountryBanWarning={auctionCountryBanWarning}
                  cardDetails={cardDetails}
                  isPreorder={isPreorder}
                  listingFormat={listingFormat}
                  autoDeclineBelow={autoDeclineBelow}
                />
              </div>
            </div>
            {seller && (
              <div className="w-full max-w-[1200px] lg:max-w-[1600px] mx-auto px-6 lg:px-8 mt-10 lg:mt-12">
                <ListingSuggestionsLazy
                  currentListingId={itemId}
                  sellerId={seller.id}
                  sellerUsername={seller.username}
                  currentUserId={currentUserId}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {currentUserId && itemId && auction && (
        <AuctionWinnerCheckoutSheet
          open={auctionCheckoutOpen}
          onOpenChange={setAuctionCheckoutOpen}
          listingId={itemId}
          userId={currentUserId}
          winnerId={auction?.highestBidderId ?? undefined}
          winningAmount={auction.highestBid ?? auction.startPrice}
          listingTitle={title}
          listingImages={imageRefs}
          sellerId={seller?.id ?? ""}
          sellerName={seller?.username ?? ""}
          sellerAvatarUrl={seller?.avatar_url}
          condition={condition}
          description={description}
          grade={grading}
          currency={currency ?? "USD"}
          auctionStatus={auction.status}
        />
      )}
    </>
  )
}
